import { Clipboard } from "@capacitor/clipboard";
import { Trans, t } from '@lingui/macro';
import { Languages, MessagesSquare, Search, Info, XCircle, CheckCircle, Send, Sparkles, FileText, ChevronRight } from "lucide-react";
import { useEffect, useReducer, useRef, useState } from "react";
import { Soribot } from "../../models/Soribot";
import { UserBlock } from "../../models/UserBlock";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { LongPress } from "../../services/Utils/LongPress";
import { Util } from "../../services/Utils/Util";
import { UtilApp } from "../../services/Utils/UtilApp";
import { SendFile } from '../../models/Message';
import { UIMessage } from "../components/UIMessage";
import { RoomRefreshEmitter } from "../rooms/RoomEvents";
import BotSourceTextModal from "../rooms/right-menu/BotSourceTextModal";
import BotMessageInfoModal from "../soribots/BotMessageInfoModal";
import MessageEmailItem from "./MessageEmailItem";
import MessageFileItem from "./MessageFileItem";
import MessagePopover from "./MessagePopover";
import { PlayMessageButton } from "./PlayMessageButton";
import { FileIcon } from "./MessageFileItem";
import { useAppUIContext } from '../../context/AppUIContext';
import { Button } from "../../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Progress } from "../../components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "../../components/ui/dialog";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

export type MessageItemState = 'new' | 'playing' | 'paused' | 'old' | 'favorite';

interface IMessageItem {
  m: UIMessage,
  state: MessageItemState,
  onMessageClick: (msg: UIMessage) => void,
  parent?: UIMessage,
  isViewButton?: boolean,
  progressForFile?: { [key: string]: SendFile }
  onResendMessage?: (msg: UIMessage) => void,
  onReplyToClick?: (targetId: string) => void,
  highlightedReaction?: { messageId: string; emoticon: string } | null,
  reactionSlot?: React.ReactNode, // 🔧 아바타 아래 리액션 버튼 슬롯
}

const MessageItem: React.FC<IMessageItem> = ({ m, state, onMessageClick, parent, progressForFile, onResendMessage, onReplyToClick, isViewButton = false, highlightedReaction, reactionSlot }) => {
  const isBlocked = My.UserBlocks.get(m.msg.user.id);
  const isDeleted = My.DeletedMessages.get(m.msg.id);
  const isNew = m.isNew();

  const [messageText, setMessageText] = useState<string | null>(null);
  const [showUnblockAlert, setShowUnblockAlert] = useState(false);
  const [showTranslateError, setShowTranslateError] = useState(false);
  const [showSummarizeError, setShowSummarizeError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { toast } = useToast();
  const [longPress] = useState(new LongPress(() => copyTextToClipboard()));
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [expandable, setExpandable] = useState<boolean>(false);
  const divRef = useRef<HTMLDivElement>(null);
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  const [summarizedText, setSummarizedText] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const { setMessageToShow } = useAppUIContext();


  useEffect(() => {
    setMessageText(getMessageText(m.msg.text));
    if (!m.isNew()) {
      setTimeout(() => {
        forceUpdate(); // Update again because the related notifiation object comes shortly afterwards, which will update isNew.
      }, 700);
    }
  }, [])

  useEffect(() => {
    const divHeight = divRef.current?.offsetHeight ?? 0;
    setExpandable(!!messageText && !messageText.includes('src=') && divHeight > 260);
  }, [messageText])

  if (isDeleted)
    return <></>
  else if (m.msg.senderEmail)
    return <MessageEmailItem m={m} />
  else if (m.msg.autoMessage) {
    return (
      <div className="text-center py-2.5 px-5 text-sm text-muted-foreground bg-muted rounded-xl" id={`msg-${m.msg.id}`}>
        {m.msg.autoMessage == 'joined' && <><Trans>{m.msg.user.name} has joined.</Trans>{m.msg.autoMessageUser && <Trans>Invited by {m.msg.autoMessageUser.name}.</Trans>}</>}
        {m.msg.autoMessage == 'left' && <><Trans>{m.msg.user.name} has left.</Trans></>}
        {m.msg.autoMessage == 'removed' && <><Trans>{m.msg.user.name} has removed {m.msg.autoMessageUser?.name}.</Trans></>}
        {m.msg.autoMessage == 'added' && <><Trans>{m.msg.user.name} has added {m.msg.autoMessageUser?.name}.</Trans></>}
        {m.msg.autoMessage == 'call-start' && <><Trans>{m.msg.user.name} started a call.</Trans></>}
        {m.msg.autoMessage == 'memo-created' && <><Trans>{m.msg.user.name} created a new memo: "{m.msg.text}".</Trans></>}
        {m.msg.autoMessage == 'memo-updated' && <><Trans>{m.msg.user.name} updated a memo: "{m.msg.text}".</Trans></>}
        {m.msg.autoMessage == 'pagecall-created' && <><Trans>{m.msg.user.name} created a Pagecall meeting: "{m.msg.text}".</Trans></>}
        <div className="text-[10px] text-gray-400 mt-1">{UtilApp.dateStrToTimeAndDay(m.msg.created, My.user.lang)}</div>
      </div>
    )
  } else {
    return (
      <div className={cn(
        "text-sm font-medium leading-relaxed text-foreground",
        state
      )} id={`msg-${m.msg.id}`}>
        {isBlocked && (
          <div className="pl-4 text-gray-500 cursor-pointer" onClick={() => setShowUnblockAlert(true)}>
            {parent && <img src="/assets/spc/keyboard-return.svg" className="inline-block w-4 h-4 mr-2" />}
            <Trans>Blocked message by {m.msg.user.name}</Trans>
          </div>
        )}
        {!isBlocked && <>
          {isNew && <img className="absolute top-0 right-0 w-5 h-5" src="assets/icon/ic-new-red-18.svg" />}
          <span className="float-right flex items-center gap-1">
            {!m.isLocal && <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="text-orange-400 hover:text-orange-500 disabled:opacity-50"
                    size="sm"
                    variant="ghost"
                    disabled={isTranslating || isSummarizing}
                    onClick={(e) => e.stopPropagation()} // Stop propagation to prevent message selection toggle
                  >
                    {(isTranslating || isSummarizing) ? (
                      <div className="animate-spin h-4 w-4 border-2 border-orange-400 border-t-transparent rounded-full" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="z-[70]">
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); translateMessage(); }}>
                    <Languages className="h-4 w-4 mr-2" />
                    <Trans>Translate</Trans>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); summarizeMessage(); }}>
                    <Sparkles className="h-4 w-4 mr-2" />
                    <Trans>Summarize</Trans>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {isViewButton && (
                <Button className="text-orange-400 hover:text-orange-500" onClick={viewMessageGroup} variant="ghost" size="sm">
                  <MessagesSquare className="h-4 w-4 mr-1" />
                </Button>
              )}
              <MessagePopover m={m} state={state} />
            </>
            }
            {m.isLocal && <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />}
          </span>
          {/* 🔧 2열 레이아웃: 왼쪽(아바타+하트), 오른쪽(이름/시간/내용) */}
          <div className="flex gap-3 cursor-pointer" onClick={() => onMessageClick(m)}>
            {/* 왼쪽 열: 아바타 + 리액션 버튼 */}
            <div className="flex flex-col items-center shrink-0 w-10">
              <Avatar className="w-10 h-10 rounded-[10px]">
                <AvatarImage src={m.msg.user.photoUrl || Util.ANONYMOUS_PHOTO_URL} className="rounded-[10px]" />
                <AvatarFallback className="rounded-[10px]">{m.msg.user.name?.[0]}</AvatarFallback>
              </Avatar>
              {/* 리액션 버튼 슬롯 */}
              {reactionSlot && (
                <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                  {reactionSlot}
                </div>
              )}
            </div>

            {/* 오른쪽 열: 이름/시간 + 내용 */}
            <div className="flex-1 min-w-0">
              {/* 첫번째 행: 이름, 시간 */}
              <div>
                <MessageUserNames />
                <div className={cn("text-[10px] text-gray-400", parent ? "" : "pt-0.5")}>
                  {UtilApp.dateStrToTimeAndDay(m.msg.created, My.user.lang)}
                </div>
              </div>

              {/* 두번째 행: 메시지 내용 */}
              <div className="mt-2">
                <ShowEmoji />
                <div className={cn(parent && "ml-5")}>
                  <PlayMessageButton m={m} />
                </div>
                {messageText && <div className={cn("relative border-0 p-0 m-0 inline-block text-sm font-medium leading-relaxed text-foreground cursor-pointer", expandable && "max-h-[250px] overflow-hidden relative")} ref={divRef}>
                  {m.msg?.user?.id?.startsWith('ai-docubot') && <span className="float-right"><BotSourceTextModal text={m.msg.text} /></span>}
                  <div className="whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: messageText }} onTouchStart={() => longPress.touchStart()} onTouchEnd={() => longPress.touchEnd()}></div>
                  {translatedText && <div className="mt-3 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                    <div className="text-sm text-blue-900 whitespace-pre-wrap"><Languages className="inline h-4 w-4 mr-1" />{translatedText}</div>
                  </div>
                  }
                  {summarizedText && <div className="mt-3 p-3 bg-green-50 rounded-lg border-l-4 border-green-400">
                    <div className="text-sm text-green-900 whitespace-pre-wrap"><Sparkles className="inline h-4 w-4 mr-1" />{summarizedText}</div>
                  </div>
                  }
                  {expandable && <><div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-50"></div><div className="absolute bottom-2 left-1/2 -translate-x-1/2 cursor-pointer" onClick={(e) => { e.stopPropagation(); setExpandable(false); }}><img src='assets/icon/ic-arrow-small-down-gray-24.svg' /></div></>}
                </div>}
                {m.msg.files?.length > 0 && <div className="message-files flex flex-wrap gap-2 mt-2">{m.msg.files.sort((a, b) => a.filename < b.filename ? -1 : 1).map((file, i) => <MessageFileItem file={file} key={i} files={m.msg.files} showTitle={false} />)}</div>}
                {m.sendFiles.length > 0 && <div className="added-files-preview flex flex-wrap gap-2 mt-2">{m.sendFiles.map((file, i) => getFileItem(file, i))}</div>}
                {getVideoElement(m)}
                <GetReactionSummary emotes={m.reactions()} highlightedEmoticon={highlightedReaction?.messageId === m.msg.id ? highlightedReaction.emoticon : null} />
                <AIComments m={m} />
                {m.msg.inProgress && <div className="animate-spin h-5 w-5 border-2 border-gray-400 border-t-transparent rounded-full mt-2" />}
              </div>
            </div>
          </div>
        </>}

        {/* Unblock Alert Dialog */}
        {isBlocked && (
          <AlertDialog open={showUnblockAlert} onOpenChange={setShowUnblockAlert}>
            <AlertDialogContent className="z-[70]">
              <AlertDialogHeader>
                <AlertDialogTitle><Trans>Do you want to unblock {isBlocked.blockedUser.name} to see messages?</Trans></AlertDialogTitle>
                <AlertDialogDescription>
                  <Trans>Messages by unblocked users are hidden for you. We don't inform {isBlocked.blockedUser.name} that you have unblocked.</Trans>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                <AlertDialogAction onClick={() => removeUserBlock(isBlocked)}><Trans>Unblock</Trans></AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        {/* Translation Error Dialog */}
        <AlertDialog open={showTranslateError} onOpenChange={setShowTranslateError}>
          <AlertDialogContent className="z-[70]">
            <AlertDialogHeader>
              <AlertDialogTitle><Trans>Translation failed</Trans></AlertDialogTitle>
              <AlertDialogDescription>{errorMessage}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction><Trans>OK</Trans></AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Summarize Error Dialog */}
        <AlertDialog open={showSummarizeError} onOpenChange={setShowSummarizeError}>
          <AlertDialogContent className="z-[70]">
            <AlertDialogHeader>
              <AlertDialogTitle><Trans>Summary failed</Trans></AlertDialogTitle>
              <AlertDialogDescription>{errorMessage}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction><Trans>OK</Trans></AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  function getFileItem(file: File, index: number) {
    let fileKey = `${m.msg.id}/${file.name}`;
    if (!progressForFile || !progressForFile[fileKey])
      return null;
    const sendFile = progressForFile[fileKey];
    let progress = progressForFile[fileKey].progress;

    return <div key={index} className="file-preview relative bg-gray-50 rounded-lg p-3 border">
      {file.type.includes('image')
        ?
        <img src={URL.createObjectURL(file)} title={file.name} className="thumb max-h-24 rounded" />
        :
        <>
          <FileIcon filename={file.name} />
          <p className="file-name text-sm mt-2">{file.name}</p>
          <p className="text-xs text-gray-500">{Util.niceBytes(file.size)}</p>
        </>
      }
      {progress < 1 && <Progress value={progress * 100} className="mt-2" />}
      {progress == 1 && <CheckCircle className="absolute top-2 right-2 h-5 w-5 text-green-500" />}
    </div>
  }

  function ShowEmoji() {
    if (m.msg.emoji)
      return <div>
        <img src={`assets/emoji/${m.msg.emoji}.png`} className="w-16 h-16" />
      </div>
    else
      return <></>
  }

  function MessageUserNames() {
    return <div className="flex items-center gap-1.5 flex-wrap">
      <div className="font-semibold text-orange-600 bg-orange-50 px-1 rounded">{m.msg.user.name}</div>
      {m.msg.replyTo &&
        <div className="flex items-center text-xs text-gray-500">
          <ChevronRight className="w-3 h-3 mx-0.5 text-gray-400" />
          <span
            className="text-orange-600 font-semibold bg-orange-50 px-1 rounded cursor-pointer hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              if (m.msg.replyToMessageId && onReplyToClick) {
                onReplyToClick(m.msg.replyToMessageId);
              }
            }}
          >
            {m.msg.replyTo.name}
          </span>
        </div>
      }
    </div>
  }

  function getVideoElement(m: UIMessage) {
    if (m.msg.videoUrl) {
      if (UtilApp.canOnlyPlayMP4() && !m.msg.videoUrl.includes('.mp4') && !m.msg.videoUrlProcessed?.includes('.mp4')) {
        return <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-900">
          <Info className="h-4 w-4" />
          <Trans>Video is being processed for iOS Safari.</Trans>
        </div>
      }
      return <div className="mt-2">
        <video controls={true} className="block max-w-[300px] max-h-[200px] mx-5 my-2.5 rounded-[5px]">
          {m.msg.videoUrlProcessed && <source src={`${m.msg.videoUrlProcessed}#t=0.1`} type={m.msg.videoUrlProcessed.includes('.mp4') ? 'video/mp4' : 'video/webm'} />}
          <source src={`${m.msg.videoUrl}#t=0.1`} type={m.msg.videoUrl.includes('.mp4') ? 'video/mp4' : 'video/webm'} />
        </video>
      </div>
    }
    else
      return <></>
  }

  async function viewMessageGroup() {
    setMessageToShow(m.msg);
  }

  async function removeUserBlock(block: UserBlock) {
    My.UserBlocks.delete(block.blockedUser.id);
    await Fire.deleteUserBlock(My.user.id, block.id);
    RoomRefreshEmitter.emit(m.msg.roomId)

    toast({
      title: t`Unblocked ${block.blockedUser.name}`,
      duration: 1000,
    })
    setShowUnblockAlert(false);
  }

  async function copyTextToClipboard() {
    if (m.msg.text) {
      await Clipboard.write({
        string: m.msg.text
      });
      toast({
        title: t`Copied to clipboard`,
        duration: 1500,
      });
    }
  }

  async function translateMessage() {
    if (!m.msg.text) return;
    setIsTranslating(true);
    setTranslatedText(null);
    const languageName = UtilApp.getLanguageName(My.user.lang);
    const message = `Translate this text to ${languageName}. \n\n${m.msg.text}`

    Fire.bot.askChatGPT(message).then(answer => {
      setIsTranslating(false);
      if (!answer || answer.trim() === '') {
        setErrorMessage(t`Could not translate to ${languageName}.`);
        setShowTranslateError(true);
        return;
      }

      const translatedText = answer.trim();
      setTranslatedText(translatedText);
    }).catch(error => {
      setErrorMessage(error.message || t`Could not translate to ${languageName}.`);
      setShowTranslateError(true);
      setIsTranslating(false);
    });
  }

  async function summarizeMessage() {
    if (!m.msg.text) return;
    setIsSummarizing(true);
    setSummarizedText(null);
    const text = translatedText || m.msg.text;
    const message = t`Summarize this text to a short paragraph.` + `\n\n${text}`

    Fire.bot.askChatGPT(message).then(answer => {
      setIsSummarizing(false);
      if (!answer || answer.trim() === '') {
        setErrorMessage(t`Could not summarize the message.`);
        setShowSummarizeError(true);
        return;
      }

      const summarizedText = answer.trim();
      setSummarizedText(summarizedText);
    }).catch(error => {
      setErrorMessage(error.message || t`Could not summarize the message.`);
      setShowSummarizeError(true);
      setIsSummarizing(false);
    });
  }
}

const AIComments: React.FC<{ m: UIMessage }> = ({ m }) => {
  const [showAll, setShowAll] = useState(Util.nrMinAgo(m.msg.created) < 3);
  const shortTextLimit = 100;

  return <>
    {m.msg.comments?.map((c, i) => <div className="ai-comment mt-2" style={{ paddingLeft: m.msg.parentMessageId ? 25 : 5 }} key={i}>
      <div onClick={() => setShowAll(!showAll)} className={cn("flex items-start gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer", showAll ? "show-all" : "show-less")}>
        <Avatar className="w-8 h-8 shrink-0">
          <AvatarImage src={c.imageUrl ?? Soribot.DEFAULT_IMAGE_URL} />
          <AvatarFallback>AI</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <small className="font-medium text-gray-700">{c.name}</small>
          {showAll && <div className="textarea text-sm mt-1 whitespace-pre-wrap">{c.text}</div>}
          {!showAll && <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{c.text?.length > shortTextLimit ? (c.text.substring(0, shortTextLimit) + t`... more`) : c.text}</div>}
        </div>
        <span className="shrink-0">
          {c.botMessageId && <BotMessageInfoModal botMessageId={c.botMessageId} bmsg={null} />}
        </span>
      </div>
    </div>)}
  </>
}

export function getMessageText(msgText: string | undefined): string {
  let text = msgText?.split('Sources:')[0];
  return UtilApp.getTextAsHTML(text?.trim(), My.user.id)
}

const GetReactionSummary: React.FC<{ emotes: UIMessage[], highlightedEmoticon?: string | null }> = ({ emotes, highlightedEmoticon }) => {
  const [showModal, setShowModal] = useState(false);

  if (emotes.length > 0) {
    let smiles = emotes.filter(m => m.msg.emoticon == 'smile').length;
    let hearts = emotes.filter(m => m.msg.emoticon == 'heart').length;
    let thumbs = emotes.filter(m => m.msg.emoticon == 'thumbs-up').length;
    let hands = emotes.filter(m => m.msg.emoticon == 'shake-hand').length;
    let checks = emotes.filter(m => m.msg.emoticon == 'check').length;
    let rockets = emotes.filter(m => m.msg.emoticon == 'rocket').length;
    let parties = emotes.filter(m => m.msg.emoticon == 'party').length;
    let hasNew = !!emotes.find(m => m.isNew());

    // 🔧 로컬 리액션 체크 (아직 서버에서 확인 안됨)
    const localEmoticons = emotes.filter(m => m.isLocal).map(m => m.msg.emoticon);

    // 🔧 강조 애니메이션 스타일
    const getHighlightClass = (emoticon: string) => {
      if (highlightedEmoticon === emoticon) {
        return "animate-bounce bg-orange-100 ring-2 ring-orange-400 rounded-lg p-1";
      }
      return "";
    };

    // 🔧 로컬 로딩 애니메이션 스타일 (fade in/out)
    const getLoadingClass = (emoticon: string) => {
      if (localEmoticons.includes(emoticon as any)) {
        return "animate-pulse opacity-60";
      }
      return "";
    };

    return <>
      <div className={cn("flex flex-wrap gap-2 p-2 bg-gray-50 rounded-lg cursor-pointer mt-[3px] w-fit", hasNew && "border-2 border-orange-400")} onClick={() => setShowModal(true)}>
        {smiles > 0 && <div className={cn("flex items-center gap-1", getHighlightClass('smile'), getLoadingClass('smile'))}><img src="/assets/icon/ic-smile-24.svg" className="w-5 h-5" /> <span className="text-sm">{smiles}</span></div>}
        {hearts > 0 && <div className={cn("flex items-center gap-1", getHighlightClass('heart'), getLoadingClass('heart'))}><img src="/assets/icon/ic-heart-24.svg" className="w-5 h-5" /> <span className="text-sm">{hearts}</span></div>}
        {thumbs > 0 && <div className={cn("flex items-center gap-1", getHighlightClass('thumbs-up'), getLoadingClass('thumbs-up'))}><img src="/assets/icon/ic-thumbs-up-24.svg" className="w-5 h-5" /> <span className="text-sm">{thumbs}</span></div>}
        {hands > 0 && <div className={cn("flex items-center gap-1", getHighlightClass('shake-hand'), getLoadingClass('shake-hand'))}><img src="/assets/icon/ic-shake-hand-24.svg" className="w-5 h-5" /> <span className="text-sm">{hands}</span></div>}
        {checks > 0 && <div className={cn("flex items-center gap-1", getHighlightClass('check'), getLoadingClass('check'))}><img src="/assets/icon/ic-check-24.svg" className="w-5 h-5" /> <span className="text-sm">{checks}</span></div>}
        {rockets > 0 && <div className={cn("flex items-center gap-1", getHighlightClass('rocket'), getLoadingClass('rocket'))}>🚀 <span className="text-sm">{rockets}</span></div>}
        {parties > 0 && <div className={cn("flex items-center gap-1", getHighlightClass('party'), getLoadingClass('party'))}>🎉 <span className="text-sm">{parties}</span></div>}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle><Trans>Reactions</Trans></DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {emotes.sort((a, b) => a.msg.created < b.msg.created ? 1 : -1).map(m => {
              let imgfile = `/assets/icon/ic-${m.msg.emoticon}-24.svg`;

              return <div key={m.msg.id} className={cn("flex items-center gap-3 p-3 rounded-lg", m.isNew() && "border-l-4 border-l-red-500 bg-red-50")}>
                <img src={imgfile} className="w-6 h-6 shrink-0" />
                <Avatar className="w-8 h-8 shrink-0">
                  <AvatarImage src={m.msg.user.photoUrl || Util.ANONYMOUS_PHOTO_URL} />
                  <AvatarFallback>{m.msg.user.name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-sm">{m.msg.user.name}</span>
                  <div className="text-xs text-gray-500">{UtilApp.dateStrToTimeAndDay(m.msg.created, My.user.lang)}</div>
                </div>
              </div>
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  }
  else
    return <></>
}

export default MessageItem;